import { ComponentType } from "react";
import {
  CheckCircleOutline as VerifiedIcon,
  Spa as LeafIcon,
  SellOutlined as ListingIcon,
  PaidOutlined as BidIcon,
  CircleOutlined as ActiveIcon,
  HighlightOff as CancelledIcon,
  LockOutlined as BlockedIcon,
  SyncAlt as ChangedIcon,
  AccessTime as ExpiredIcon,
  Forest as TreeIcon,
  AttachMoney as FundsIcon,
  HandshakeOutlined as SoldIcon,
  DescriptionOutlined as DocumentIcon,
  Inventory2Outlined as TransferredIcon,
  WavingHandOutlined as InvitedIcon,
  MapOutlined as LandIcon,
  PlaceOutlined as ClaimedIcon,
  WarningAmberOutlined as WarningIcon,
  ReportProblemOutlined as UrgentIcon,
  HourglassEmpty as PendingIcon,
  OutlinedFlag as FlagIcon,
  InfoOutlined as DefaultIcon,
} from "@mui/icons-material";

import UploadDraftIcon from "./UploadDraftIcon";

// Mirrors the backend contract documented in
// docs/notification-frontend-implementation.md. Keep this list in lock-step with
// the server's NotificationType enum.
export type NotificationType =
  // Guardian
  | "GUARDIAN_INVITED"
  | "EFT_VERIFIED_PENDING_MINT"
  | "EFT_CREATED"
  | "EFT_LISTED_TO_AUCTION"
  | "EFT_NEW_BID"
  | "EFT_NOW_ACTIVE"
  | "EFT_AUCTION_CANCELLED"
  | "EFT_BLOCKED"
  | "EFT_SUCCESSFULLY_VERIFIED"
  | "EFT_CANCELLED"
  | "EFT_GUARDIAN_CHANGED"
  | "EFT_EXPIRED"
  | "TR3_AVAILABLE_TO_MINT"
  | "TR3_MINTED"
  | "USD_FUNDS_REDEEMED"
  // Patron
  | "EFT_LISTED_TO_OFFER"
  | "EFT_SOLD_ON_OFFER"
  | "EFT_OFFER_PLACED"
  | "EFT_OFFER_CANCELLED"
  | "EFT_SUCCESSFULLY_TRANSFERRED"
  // Referrer
  | "REFERRER_EFT_CREATED"
  | "REFERRER_EFT_ACTIVE"
  | "REFERRER_TR3_MINTED"
  // Trustee
  | "TRUSTEE_INVITED_TO_ORG"
  | "TRUSTEE_INVITED"
  | "NEW_LAND_TO_CLAIM"
  | "LAND_CLAIMED"
  | "EFT_VERIFICATION_REQUIRED"
  | "EFT_VERIFICATION_COMPLETED"
  | "EFT_VERIFICATION_PENDING_URGENT"
  | "EFT_VERIFICATION_DELAYED_URGENT"
  | "EFT_CREATED_AFTER_WATCHDOG"
  | "EFT_ACTIVE_AS_TRUST"
  | "ACTIVE_EFT_ASSIGNED"
  | "FLAG_RECEIVED"
  | "TRUSTEE_WALLET_BLOCKED"
  // Trust Admin
  | "TRUST_ADMIN_INVITED"
  | "NEW_TRUSTEE_PENDING_APPROVAL"
  | "EFT_REQUIRES_ACTION"
  | "EFT_REASSIGNED"
  | "TRUSTEE_FLAGGED"
  | "TRUSTEE_REQUIRES_ACTION"
  | "TRUSTEE_WALLET_BLOCKED_BY_ADMIN"
  | "TRUST_ADMIN_WALLET_BLOCKED"
  // Global Trust Admin
  | "NEW_TRUST_ADMIN_PENDING"
  | "GTA_ADDED"
  // Land upload wizard: one row per draft land, rewritten on every draft save
  | "LAND_UPLOAD_IN_PROGRESS";

export type NotificationEntityType = "EFT" | "AUCTION" | "TRUST" | null;

export interface Notification {
  id: string; // UUID
  recipientAddress: string;
  type: NotificationType;
  title: string;
  body: string;
  entityId: string | null; // tokenId, auctionId, etc.
  entityType: NotificationEntityType;
  metadata: Record<string, any> | null;
  isRead: boolean;
  isDismissed: boolean;
  createdAt: string; // ISO 8601
  // Resolved by the API for EFT notifications (NotificationService.withLand). entityId is
  // the land id for pre-mint types but the on-chain NFT id for everything raised from a
  // contract event, so landId is the one to link with. Optional: older API builds omit them.
  landId?: number | null;
  contentThumb?: string | null; // the land's static WebP ≤256px; null pre-rendition
  // LAND_UPLOAD_IN_PROGRESS only, also resolved at read time. contentThumb stays null
  // until the land-image step is in completedSteps; landName is null until the draft has one.
  progress?: NotificationProgress | null;
  landName?: string | null;
}

/** How far a draft has got through the upload wizard (pages/Scan's four steps). */
export interface NotificationProgress {
  /** Finished step indexes, ascending: 0 ownership, 1 location, 2 description, 3 easement. */
  completedSteps: number[];
  totalSteps: number;
}

export const isUploadProgress = (type: NotificationType): boolean => type === "LAND_UPLOAD_IN_PROGRESS";

export interface NotificationListResponse {
  data: Notification[];
  total: number;
  page: number;
  limit: number;
}

export interface UnreadCountResponse {
  unread: number;
}

// The two types that demand the prominent "urgent" treatment from the design
// (red/warning accent on the row + a pinned banner above the feed).
export const URGENT_TYPES: NotificationType[] = [
  "EFT_VERIFICATION_PENDING_URGENT",
  "EFT_VERIFICATION_DELAYED_URGENT",
];

export const isUrgent = (type: NotificationType): boolean => URGENT_TYPES.includes(type);

// Per-type icon. The Figma design renders the left-hand badge as a line icon
// (shield for Guardian status, info circle for invites, file/upload for the
// progress alert), so we map each type to the closest MUI outlined icon rather
// than the spec's placeholder emoji.
export const NOTIFICATION_ICONS: Partial<Record<NotificationType, ComponentType>> = {
  // Guardian
  GUARDIAN_INVITED: InvitedIcon,
  EFT_VERIFIED_PENDING_MINT: VerifiedIcon,
  EFT_CREATED: LeafIcon,
  EFT_LISTED_TO_AUCTION: ListingIcon,
  EFT_NEW_BID: BidIcon,
  EFT_NOW_ACTIVE: ActiveIcon,
  EFT_AUCTION_CANCELLED: CancelledIcon,
  EFT_BLOCKED: BlockedIcon,
  EFT_SUCCESSFULLY_VERIFIED: VerifiedIcon,
  EFT_CANCELLED: CancelledIcon,
  EFT_GUARDIAN_CHANGED: ChangedIcon,
  EFT_EXPIRED: ExpiredIcon,
  TR3_AVAILABLE_TO_MINT: TreeIcon,
  TR3_MINTED: TreeIcon,
  USD_FUNDS_REDEEMED: FundsIcon,
  // Patron
  EFT_LISTED_TO_OFFER: ListingIcon,
  EFT_SOLD_ON_OFFER: SoldIcon,
  EFT_OFFER_PLACED: DocumentIcon,
  EFT_OFFER_CANCELLED: CancelledIcon,
  EFT_SUCCESSFULLY_TRANSFERRED: TransferredIcon,
  // Referrer
  REFERRER_EFT_CREATED: LeafIcon,
  REFERRER_EFT_ACTIVE: ActiveIcon,
  REFERRER_TR3_MINTED: TreeIcon,
  // Trustee
  TRUSTEE_INVITED_TO_ORG: InvitedIcon,
  TRUSTEE_INVITED: InvitedIcon,
  NEW_LAND_TO_CLAIM: LandIcon,
  LAND_CLAIMED: ClaimedIcon,
  EFT_VERIFICATION_REQUIRED: DocumentIcon,
  EFT_VERIFICATION_COMPLETED: VerifiedIcon,
  EFT_VERIFICATION_PENDING_URGENT: WarningIcon,
  EFT_VERIFICATION_DELAYED_URGENT: UrgentIcon,
  EFT_CREATED_AFTER_WATCHDOG: LeafIcon,
  EFT_ACTIVE_AS_TRUST: ActiveIcon,
  ACTIVE_EFT_ASSIGNED: DocumentIcon,
  FLAG_RECEIVED: FlagIcon,
  TRUSTEE_WALLET_BLOCKED: BlockedIcon,
  // Trust Admin
  TRUST_ADMIN_INVITED: InvitedIcon,
  NEW_TRUSTEE_PENDING_APPROVAL: PendingIcon,
  EFT_REQUIRES_ACTION: WarningIcon,
  EFT_REASSIGNED: ChangedIcon,
  TRUSTEE_FLAGGED: FlagIcon,
  TRUSTEE_REQUIRES_ACTION: WarningIcon,
  TRUSTEE_WALLET_BLOCKED_BY_ADMIN: BlockedIcon,
  TRUST_ADMIN_WALLET_BLOCKED: BlockedIcon,
  // Global Trust Admin
  NEW_TRUST_ADMIN_PENDING: PendingIcon,
  GTA_ADDED: InvitedIcon,
  // Land upload wizard
  LAND_UPLOAD_IN_PROGRESS: UploadDraftIcon,
};

// The Guardian-status badge in the design is the only one tinted purple; every
// other badge is neutral grey. Surface that distinction so NotificationItem can
// pick the accent without re-deriving it.
export const PURPLE_BADGE_TYPES: NotificationType[] = [
  "GUARDIAN_INVITED",
  "EFT_GUARDIAN_CHANGED",
  "TRUSTEE_INVITED",
  "TRUSTEE_INVITED_TO_ORG",
  "TRUST_ADMIN_INVITED",
  "GTA_ADDED",
];

export const iconForType = (type: NotificationType): ComponentType =>
  NOTIFICATION_ICONS[type] ?? DefaultIcon;

// Compact relative-time label ("just now", "4 days", …). The design renders bare
// counts like "4 days" / "11 days" (no "ago" suffix) so we match that wording.
export const formatRelative = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ${hrs === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} ${days === 1 ? "day" : "days"}`;
  return new Date(isoDate).toLocaleDateString();
};
